import React, { useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { X } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import type { NavGroup } from '../../../config/navigation';
import { MobileNavSection } from './MobileNavSection';
import { useAuth } from '../../../hooks/useAuth';
import { useImageUrl } from '../../../hooks/useImageUrl';
import { usersService } from '../../../services/membersService';
import { Avatar } from '../../ui/Avatar';

interface MobileNavPanelProps {
  isOpen: boolean;
  onClose: () => void;
  groups: NavGroup[];
}

const MobileNavPanel: React.FC<MobileNavPanelProps> = ({ isOpen, onClose, groups }) => {
  const { user, isAuthenticated, logout } = useAuth();
  const location = useLocation();
  const prevPathRef = useRef(location.pathname);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (prevPathRef.current !== location.pathname) {
      prevPathRef.current = location.pathname;
      onCloseRef.current();
    }
  }, [location.pathname]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onCloseRef.current();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen]);

  const { data: userWithMember } = useQuery({
    queryKey: ['user-with-member', user?.id],
    queryFn: () => usersService.getUserWithMember(user!.id),
    enabled: isAuthenticated && !!user?.id,
    staleTime: 5 * 60 * 1000,
  });
  const profileImageUrl = useImageUrl(userWithMember?.familyMember?.profileImage);

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/40 z-40 lg:hidden animate-fade-in"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Slide-out Panel */}
      <div
        className="fixed top-0 right-0 bottom-0 w-80 max-w-[85vw] bg-white z-50 shadow-2xl lg:hidden animate-slide-in-right flex flex-col"
        role="dialog"
        aria-modal="true"
        aria-label="Navigation menu"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-border">
          <h2 className="text-lg font-semibold text-text-primary">Menu</h2>
          <button
            onClick={onClose}
            className="p-2 -mr-2 hover:bg-surface-1 rounded-md transition-colors"
            aria-label="Close menu"
          >
            <X className="w-5 h-5 text-text-secondary" />
          </button>
        </div>

        {/* Navigation Sections */}
        <div className="flex-1 overflow-y-auto px-3 py-4 space-y-1">
          {groups.map((group) => (
            <MobileNavSection
              key={group.id}
              group={group}
              onNavigate={onClose}
            />
          ))}
        </div>

        {/* User Profile & Auth */}
        <div className="px-5 py-4 border-t border-border">
          {isAuthenticated && user ? (
            <div className="space-y-3">
              <Link
                to="/profile"
                className="flex items-center gap-3 p-2 -mx-2 rounded-lg hover:bg-surface-1 transition-colors"
                onClick={onClose}
              >
                <Avatar
                  firstName={user.firstName}
                  lastName={user.lastName}
                  src={profileImageUrl}
                  size="sm"
                  className="border-0"
                />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-text-primary truncate">
                    {user.firstName} {user.lastName}
                  </div>
                  <div className="text-xs text-text-tertiary">View Profile</div>
                </div>
              </Link>
              <button
                onClick={() => {
                  logout();
                  onClose();
                }}
                className="w-full bg-brand text-white px-4 py-2.5 rounded-lg text-sm font-medium hover:bg-brand-hover transition-colors"
              >
                Logout
              </button>
            </div>
          ) : (
            <Link
              to="/login"
              className="block w-full text-center bg-brand text-white px-4 py-2.5 rounded-lg text-sm font-medium hover:bg-brand-hover transition-colors"
              onClick={onClose}
            >
              Login
            </Link>
          )}
        </div>
      </div>
    </>
  );
};

export { MobileNavPanel };
